/* ============================================================
   James Oro edit bar (Sep 2026). Pairs with snippets/jo-editbar.liquid.

   Desktop: every tap applies instantly.
   Mobile: the Filter panel works on a draft. Taps fetch the new
   count in the background and update "Show N"; the grid changes
   once, on "Show N". Opening a panel adds a history entry, so the
   phone's back gesture closes it.

   Speed:
   - Panel taps fetch a count-only section (sections/jo-editbar-facets),
     not the whole grid, so "Show N" updates fast on big collections.
   - Opening a panel group prefetches the counts for its options, and each
     new count prefetches the matching grid, so "Show N" usually renders
     from memory. On desktop, hovering an option or sort prefetches it.
   - 150ms debounce, stale responses ignored, responses cached (40).
   Tracking: Clarity events jo_* and window.dataLayer
   { event: 'jo_filter', jo_action, ... }.
   ============================================================ */
(function () {
  if (customElements.get('jo-editbar')) return;

  const CACHE_LIMIT = 40;
  const COUNT_SECTION = 'jo-editbar-facets';
  const cache = new Map(); // url -> Promise<html>, shared by fetches and prefetches
  let inflight = 0;
  let controller = null;
  let draftController = null;

  function track(name, detail) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', name);
        if (detail && detail.label) window.clarity('set', name, String(detail.label).slice(0, 60));
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'jo_filter', jo_action: name }, detail || {}));
    } catch (e) { /* tracking must never break filtering */ }
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function currentQuery() {
    return window.location.search.slice(1);
  }

  function abortError() {
    return new DOMException('Aborted', 'AbortError');
  }

  function saveData() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class JoEditbar extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.dataset.sectionId;
      this.activeSheet = null;
      this.pendingQuery = currentQuery();

      this.onInput = debounce((form) => this.render(this.buildQuery(form)), 150);
      this.onDraft = (form) => this.draft(form, 150);

      this.addEventListener('change', (e) => {
        const input = e.target;
        if (!input.matches('input')) return;
        const form = input.closest('form[data-jo-form]');
        if (!form) return;
        if (input.type === 'checkbox') {
          const label = input.closest('label');
          const group = input.closest('[data-group]');
          track('jo_filter_apply', {
            label: label ? label.textContent.trim() : input.value,
            group: group ? group.dataset.group : '',
            checked: input.checked,
            surface: form.dataset.joForm === 'm' ? 'mobile' : 'desktop'
          });
        }
        if (form.dataset.joForm === 'm') this.onDraft(form);
        else this.onInput(form);
      });

      this.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-jo-form]');
        if (!form) return;
        e.preventDefault();
        if (form.dataset.joForm === 'm') this.draft(form);
        else this.render(this.buildQuery(form));
      });

      this.addEventListener('click', (e) => this.onClick(e));

      // Desktop: hovering an option or a sort starts loading it
      this.addEventListener('pointerover', (e) => {
        if (e.pointerType !== 'mouse') return;
        const target = e.target.closest('form[data-jo-form="d"] .jo-opt__label, .jo-dd--sort [data-jo-sort]');
        if (!target || target === this.hoverTarget) return;
        this.hoverTarget = target;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => {
          if (this.hoverTarget !== target) return;
          if (target.dataset.joSort) {
            this.prefetch(this.sectionUrl(this.sortQuery(target.dataset.joSort)));
            return;
          }
          const input = target.querySelector('input');
          if (!input || input.disabled) return;
          this.prefetch(this.sectionUrl(this.buildQuery(target.closest('form'), input)));
        }, 80);
      });
      this.addEventListener('pointerout', (e) => {
        if (this.hoverTarget && !this.hoverTarget.contains(e.relatedTarget)) this.hoverTarget = null;
      });

      // Desktop: one dropdown open at a time
      this.addEventListener('toggle', (e) => {
        const d = e.target;
        if (!d.matches || !d.matches('.jo-dd') || !d.open) return;
        this.querySelectorAll('.jo-dd[open]').forEach((o) => { if (o !== d) o.open = false; });
        if (d.dataset.group !== 'sort') track('jo_group_open', { label: d.dataset.group });
      }, true);

      // Mobile panel: opening a group loads the count for each of its options
      this.addEventListener('toggle', (e) => {
        const d = e.target;
        if (!d.matches || !d.matches('.jo-acc') || !d.open || !this.activeSheet) return;
        const form = d.closest('form[data-jo-form="m"]');
        if (!form || this.countSectionMissing) return;
        [...d.querySelectorAll('input[type="checkbox"]:not(:disabled)')].slice(0, 12)
          .forEach((input) => this.prefetch(this.countUrl(this.buildQuery(form, input))));
      }, true);

      document.addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('.jo-dd')) return;
        this.closeDropdowns();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.activeSheet) this.closeDropdowns(true);
      });

      // Back / forward. Capture phase + stopImmediatePropagation keeps the
      // legacy facets.js popstate handler (still loaded by the section) from
      // also firing and erroring on markup it can't find.
      window.addEventListener('popstate', (e) => {
        e.stopImmediatePropagation();
        if (this.activeSheet) {
          this.closeSheet(this.activeSheet, { revert: true, method: 'back' });
          return;
        }
        this.render(currentQuery(), { push: false });
      }, true);

      this.querySelectorAll('dialog.jo-sheet').forEach((dlg) => {
        dlg.addEventListener('cancel', (e) => { e.preventDefault(); this.dismiss('escape'); });
      });

      // Desktop: line the bar up with the product cards
      this.align();
      window.addEventListener('load', () => this.align(), { once: true });
      window.addEventListener('resize', debounce(() => this.align(), 150));
    }

    /* ---------- clicks ---------- */

    onClick(e) {
      const remove = e.target.closest('a[data-jo-remove]');
      if (remove) {
        e.preventDefault();
        const url = new URL(remove.href, window.location.origin);
        track('jo_filter_remove', { label: remove.textContent.trim() });
        this.render(this.withSort(url.searchParams).toString());
        return;
      }

      const sort = e.target.closest('[data-jo-sort]');
      if (sort) {
        e.preventDefault();
        const query = this.sortQuery(sort.dataset.joSort);
        track('jo_sort', { label: sort.textContent.trim() });
        this.closeDropdowns();
        const sheet = sort.closest('dialog');
        if (sheet) {
          this.prefetch(this.sectionUrl(query));
          this.closeSheet(sheet, { method: 'sort' }).then(() => this.render(query, { replace: true }));
        } else {
          this.render(query);
        }
        return;
      }

      const open = e.target.closest('[data-jo-open]');
      if (open) {
        this.openSheet(open.dataset.joOpen);
        return;
      }

      if (e.target.closest('[data-jo-dismiss]')) {
        this.dismiss('close');
        return;
      }

      if (e.target.closest('[data-jo-apply]')) {
        this.applyDraft();
        return;
      }

      if (e.target.closest('[data-jo-draft-clear]')) {
        const form = this.querySelector('form[data-jo-form="m"]');
        if (!form) return;
        form.querySelectorAll('input[type="checkbox"]').forEach((i) => { i.checked = false; });
        track('jo_chip_clear_all', { surface: 'sheet' });
        this.draft(form);
      }
    }

    closeDropdowns(returnFocus) {
      this.querySelectorAll('.jo-dd[open]').forEach((d) => {
        d.open = false;
        if (returnFocus) { const s = d.querySelector('summary'); if (s) s.focus(); }
      });
    }

    /* ---------- mobile panels ---------- */

    openSheet(name) {
      const dlg = this.querySelector(`dialog[data-jo-sheet="${name}"]`);
      if (!dlg || this.activeSheet) return;
      this.activeSheet = dlg;
      this.openerName = name;
      this.pendingQuery = currentQuery();
      dlg.querySelectorAll('details.jo-acc[open]').forEach((d) => { d.open = false; });
      this.snapshot = {};
      ['sheet', 'sheetfoot'].forEach((r) => {
        const el = this.querySelector(`[data-jo-region="${r}"]`);
        if (el) this.snapshot[r] = el.innerHTML;
      });
      window.history.pushState({ joSheet: name }, '', window.location.href);
      this.lockScroll();
      dlg.showModal();
      const title = dlg.querySelector('.jo-sheet__title');
      if (title) title.focus({ preventScroll: true });
      this.setExpanded(name, true);
      track(name === 'sort' ? 'jo_sort_open' : 'jo_filter_open');
    }

    // Close without applying (X, Escape, back gesture)
    dismiss(method) {
      if (!this.activeSheet) return;
      this.dismissMethod = method;
      if (window.history.state && window.history.state.joSheet) {
        window.history.back(); // popstate closes and reverts
      } else {
        this.closeSheet(this.activeSheet, { revert: true, method });
      }
    }

    closeSheet(dlg, opts = {}) {
      if (!dlg || this.activeSheet !== dlg) return Promise.resolve();
      this.activeSheet = null;
      clearTimeout(this.draftTimer);
      if (draftController) draftController.abort();
      if (opts.revert && this.snapshot) {
        Object.keys(this.snapshot).forEach((r) => {
          const el = this.querySelector(`[data-jo-region="${r}"]`);
          if (el) el.innerHTML = this.snapshot[r];
        });
        this.pendingQuery = currentQuery();
      }
      track('jo_sheet_close', { method: this.dismissMethod || opts.method || 'close', sheet: dlg.dataset.joSheet });
      this.dismissMethod = null;
      const finish = () => {
        dlg.classList.remove('is-closing');
        if (dlg.open) dlg.close();
        this.unlockScroll();
        this.setExpanded(this.openerName, false);
        const trigger = this.querySelector(`[data-jo-open="${this.openerName}"]`);
        if (trigger) trigger.focus({ preventScroll: true });
      };
      if (reducedMotion()) { finish(); return Promise.resolve(); }
      dlg.classList.add('is-closing');
      return new Promise((resolve) => setTimeout(() => { finish(); resolve(); }, 160));
    }

    async applyDraft() {
      const dlg = this.activeSheet;
      if (!dlg) return;
      // Apply the current inputs even if their debounced count request hasn't
      // started or finished. Counts are a preview, not the source of the draft.
      const form = dlg.querySelector('form[data-jo-form="m"]');
      const query = form ? this.buildQuery(form) : this.pendingQuery;
      if (query === currentQuery()) {
        this.dismiss('show');
        return;
      }
      await this.closeSheet(dlg, { method: 'show' });
      await this.render(query, { replace: true });
      this.scrollToGrid();
    }

    draft(form, wait = 0) {
      if (!this.activeSheet) return;
      const query = this.buildQuery(form);
      this.pendingQuery = query;
      const foot = this.querySelector('[data-jo-region="sheetfoot"]');
      if (foot) foot.setAttribute('aria-busy', 'true');
      // Invalidate the old response on input, before delaying the next fetch.
      // Otherwise it can replace the inputs during the debounce window.
      clearTimeout(this.draftTimer);
      if (draftController) draftController.abort();
      draftController = new AbortController();
      const signal = draftController.signal;
      const update = async () => {
        let nextBar;
        try {
          nextBar = await this.fetchCounts(query, signal);
        } catch (err) {
          if (err.name !== 'AbortError' && foot) foot.removeAttribute('aria-busy');
          return;
        }
        if (signal.aborted || !this.activeSheet) return;
        // Start loading the grid for this selection so "Show N" is instant
        this.prefetch(this.sectionUrl(query));
        if (nextBar) this.swapRegions(nextBar, ['sheet', 'sheetfoot']);
        const f = this.querySelector('[data-jo-region="sheetfoot"]');
        if (f) f.removeAttribute('aria-busy');
        const count = nextBar && nextBar.querySelector('.jo-count');
        this.announce(count ? count.textContent.trim() : '');
        if (count && /^0 /.test(count.textContent.trim())) track('jo_zero_results', { surface: 'sheet' });
      };
      if (wait) {
        this.draftTimer = setTimeout(update, wait);
        return;
      }
      return update();
    }

    setExpanded(name, on) {
      const btn = this.querySelector(`[data-jo-open="${name}"]`);
      if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    lockScroll() {
      this.lockY = window.scrollY;
      const b = document.body.style;
      b.position = 'fixed';
      b.top = `-${this.lockY}px`;
      b.left = '0';
      b.right = '0';
      b.width = '100%';
      document.documentElement.classList.add('jo-lock');
    }

    unlockScroll() {
      const b = document.body.style;
      b.position = '';
      b.top = '';
      b.left = '';
      b.right = '';
      b.width = '';
      document.documentElement.classList.remove('jo-lock');
      if (typeof this.lockY === 'number') window.scrollTo(0, this.lockY);
    }

    announce(text) {
      const live = this.querySelector('[data-jo-live]');
      if (live) live.textContent = text;
    }

    /* ---------- layout ---------- */

    align() {
      this.style.marginLeft = '';
      this.style.marginRight = '';
      if (window.innerWidth < 750) return;
      const cards = [...document.querySelectorAll('#product-grid > li.grid__item > *')].filter((el) => el.offsetParent);
      if (!cards.length) return;
      const rowTop = cards[0].getBoundingClientRect().top;
      const row = cards.filter((el) => Math.abs(el.getBoundingClientRect().top - rowTop) < 4);
      const left = row[0].getBoundingClientRect().left;
      const right = Math.max(...row.map((el) => el.getBoundingClientRect().right));
      const me = this.getBoundingClientRect();
      const dl = Math.round(left - me.left);
      const dr = Math.round(me.right - right);
      if (Math.abs(dl) > 120 || Math.abs(dr) > 120) return; // layout not what we expect, leave it
      this.style.marginLeft = dl + 'px';
      this.style.marginRight = dr + 'px';
    }

    headerBottom() {
      let bottom = 0;
      document.querySelectorAll('.shopify-section-group-header-group, .section-header').forEach((el) => {
        const pos = getComputedStyle(el).position;
        if (pos !== 'sticky' && pos !== 'fixed') return;
        const r = el.getBoundingClientRect();
        if (r.bottom > bottom && r.top < 1) bottom = r.bottom;
      });
      return Math.max(0, Math.round(bottom));
    }

    scrollToGrid() {
      const top = this.getBoundingClientRect().top - this.headerBottom();
      if (top < 0) window.scrollBy({ top: top - 8, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* ---------- data ---------- */

    withSort(params) {
      const sort = new URLSearchParams(currentQuery()).get('sort_by');
      params.delete('page');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort);
      return params;
    }

    // toggle: optional checkbox to count as flipped (for prefetching)
    buildQuery(form, toggle) {
      const params = new URLSearchParams();
      form.querySelectorAll('input').forEach((input) => {
        if (!input.name || input.disabled) return;
        if (input.type === 'checkbox') {
          const on = input === toggle ? !input.checked : input.checked;
          if (on) params.append(input.name, input.value);
        } else if (input.value !== '') {
          params.append(input.name, input.value);
        }
      });
      return this.withSort(params).toString();
    }

    sortQuery(value) {
      const params = new URLSearchParams(currentQuery());
      params.set('sort_by', value);
      params.delete('page');
      return params.toString();
    }

    sectionUrl(query) {
      return `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}${query ? '&' + query : ''}`;
    }

    countUrl(query) {
      return `${window.location.pathname}?section_id=${COUNT_SECTION}${query ? '&' + query : ''}`;
    }

    // Filter markup + count for a query. Uses the light count section; if the
    // theme doesn't have it, falls back to the full section for the session.
    async fetchCounts(query, signal) {
      if (!this.countSectionMissing) {
        try {
          const bar = new DOMParser().parseFromString(await this.fetchHtml(this.countUrl(query), signal), 'text/html').querySelector('jo-editbar');
          if (bar) return bar;
        } catch (err) {
          if (err.name === 'AbortError') throw err;
        }
        this.countSectionMissing = true;
      }
      const html = await this.fetchHtml(this.sectionUrl(query), signal);
      return new DOMParser().parseFromString(html, 'text/html').querySelector('jo-editbar');
    }

    // One network request per URL. The signal only stops the caller from
    // waiting; the response still lands in the cache for next time.
    fetchHtml(url, signal) {
      let p = cache.get(url);
      if (!p) {
        p = fetch(url, { credentials: 'same-origin' }).then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        });
        p.catch(() => { if (cache.get(url) === p) cache.delete(url); });
        cache.set(url, p);
        if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
      }
      if (!signal) return p;
      if (signal.aborted) return Promise.reject(abortError());
      return new Promise((resolve, reject) => {
        signal.addEventListener('abort', () => reject(abortError()), { once: true });
        p.then(resolve, reject);
      });
    }

    // Background load, at most 2 at a time, skipped on Data Saver.
    prefetch(url) {
      if (saveData() || cache.has(url)) return;
      if (inflight >= 2) {
        this.queue = (this.queue || []).filter((u) => u !== url);
        this.queue.push(url);
        if (this.queue.length > 16) this.queue.shift();
        return;
      }
      inflight++;
      this.fetchHtml(url).catch(() => {}).finally(() => {
        inflight--;
        const next = this.queue && this.queue.shift();
        if (next) this.prefetch(next);
      });
    }

    async render(query, opts = {}) {
      const grid = document.getElementById('ProductGridContainer');
      if (controller) controller.abort();
      controller = new AbortController();
      const signal = controller.signal;

      if (grid) grid.classList.add('jo-loading');
      this.classList.add('is-loading');

      let html;
      try {
        html = await this.fetchHtml(this.sectionUrl(query), signal);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Network or server problem: fall back to a normal page load
        window.location.href = window.location.pathname + (query ? '?' + query : '');
        return;
      }
      if (signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.getElementById('ProductGridContainer');
      const nextBar = doc.querySelector('jo-editbar');

      if (grid && nextGrid) grid.innerHTML = nextGrid.innerHTML;
      if (nextBar) this.swapRegions(nextBar);

      const pageUrl = window.location.pathname + (query ? '?' + query : '');
      if (opts.replace) window.history.replaceState(null, '', pageUrl);
      else if (opts.push !== false) window.history.pushState({ jo: true }, '', pageUrl);
      this.pendingQuery = query;

      if (grid) grid.classList.remove('jo-loading');
      this.classList.remove('is-loading');
      this.align();

      const count = nextBar && nextBar.querySelector('.jo-count');
      this.announce(count ? count.textContent.trim() : '');
      if (count && /^0 /.test(count.textContent.trim())) track('jo_zero_results', { surface: 'page' });

      document.dispatchEvent(new CustomEvent('jo:filters-rendered', { detail: { query } }));
    }

    swapRegions(nextBar, only) {
      const focusedId = document.activeElement && document.activeElement.id;
      const openGroups = new Set([...this.querySelectorAll('details[open][data-group]')].map((d) => (d.closest('dialog') ? 'm:' : 'd:') + d.dataset.group));
      const closedMobile = new Set([...this.querySelectorAll('dialog details.jo-acc:not([open])')].map((d) => d.dataset.group));
      const expanded = [...this.querySelectorAll('[data-jo-open][aria-expanded="true"]')].map((b) => b.dataset.joOpen);

      this.querySelectorAll('[data-jo-region]').forEach((region) => {
        const name = region.dataset.joRegion;
        if (only && !only.includes(name)) return;
        const next = nextBar.querySelector(`[data-jo-region="${name}"]`);
        if (next) region.innerHTML = next.innerHTML;
      });

      this.querySelectorAll('details[data-group]').forEach((d) => {
        const inSheet = !!d.closest('dialog');
        const key = (inSheet ? 'm:' : 'd:') + d.dataset.group;
        if (inSheet) {
          if (openGroups.has(key)) d.open = true;
          else if (closedMobile.has(d.dataset.group)) d.open = false;
        } else {
          d.open = openGroups.has(key);
        }
      });
      expanded.forEach((name) => this.setExpanded(name, true));

      if (focusedId) {
        const el = document.getElementById(focusedId);
        if (el) el.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('jo-editbar', JoEditbar);
})();
